import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Users, FolderKanban, CreditCard, CheckCircle2, Plus, Download, Paperclip } from "lucide-react";
import { PHASES, ROLE_LABELS } from "../lib/methodology";

const STATUS_COLORS = {
    active: "text-[#0d1628] bg-[#b89b6f]/15 border-[#b89b6f]",
    pending: "text-orange-700 bg-orange-50 border-orange-200",
    expired: "text-red-700 bg-red-50 border-red-200",
    cancelled: "text-gray-600 bg-gray-50 border-gray-200",
    none: "text-gray-600 bg-gray-50 border-gray-200",
};

export default function AdminCompanyDetailPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const nav = useNavigate();

    const load = async () => {
        try {
            const { data: res } = await api.get(`/admin/companies/${id}`);
            setData(res);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { load(); }, [id]);

    const activate = async (subId) => {
        if (!window.confirm("Ativar subscrição? A duração começa a contar agora.")) return;
        try { await api.post(`/admin/subscriptions/${subId}/activate`, { notes: "Ativada manualmente" }); toast.success("Ativada"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const extend = async (subId) => {
        const days = parseInt(window.prompt("Quantos dias adicionar?", "30"));
        if (!days || days <= 0) return;
        try { await api.post(`/admin/subscriptions/${subId}/extend`, { days }); toast.success("Estendida"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const cancel = async (subId) => {
        if (!window.confirm("Cancelar subscrição?")) return;
        try { await api.post(`/admin/subscriptions/${subId}/cancel`); toast.success("Cancelada"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const exportCompany = async () => {
        try {
            const { data: bundle } = await api.get(`/admin/companies/${id}/export`);
            const safeName = (bundle.company?.name || "company").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
            const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ihtp-${safeName}-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Exportação concluída");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (!data) return <div className="label-caps">A carregar...</div>;

    const { company, subscription, users, projects, subscriptions } = data;
    const logoUrl = company.logo_path ? fileUrl(company.logo_path) : null;

    return (
        <div className="space-y-10" data-testid="admin-company-detail-page">
            <div>
                <button onClick={() => nav("/app/super")} className="label-caps inline-flex items-center gap-2" data-testid="back-to-super">
                    <ArrowLeft size={12} /> Central do Super Admin
                </button>
                <div className="flex items-center gap-6 mt-6">
                    {logoUrl ? (
                        <img src={logoUrl} alt={company.name} className="h-16 max-w-[200px] object-contain" data-testid="company-logo-detail" />
                    ) : (
                        <div className="h-16 w-16 border border-[#e4e4e7] flex items-center justify-center font-display text-2xl text-[#0d1628]">
                            {company.name?.[0]?.toUpperCase() || "?"}
                        </div>
                    )}
                    <div className="flex-1">
                        <h1 className="font-display text-4xl md:text-5xl text-[#0d1628]" data-testid="company-detail-name">{company.name}</h1>
                        <div className="text-[#52525b] mt-2 text-sm">{company.email}{company.industry && ` · ${company.industry}`}</div>
                    </div>
                    <button onClick={exportCompany} className="btn-outline" data-testid="export-company-btn" title="Descarrega empresa + utilizadores + subscrição num ficheiro JSON">
                        <Download size={14} /> Exportar (JSON)
                    </button>
                </div>
            </div>

            {/* Subscription summary */}
            <section className={`card-flat ${subscription?.warning ? "border-[#b89b6f]" : ""}`} data-testid="company-sub-summary">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="label-caps">Subscrição atual</div>
                        <div className="flex items-baseline gap-3 mt-3">
                            <span className={`text-xs border px-2 py-1 capitalize ${STATUS_COLORS[subscription?.status] || STATUS_COLORS.none}`}>
                                {subscription?.status || "none"}
                            </span>
                            {subscription?.plan_name && <span className="font-display text-2xl text-[#0d1628]">{subscription.plan_name}</span>}
                        </div>
                        {subscription?.ends_at && (
                            <div className="text-sm text-[#52525b] mt-3">
                                Termina a <strong className="text-[#0d1628]">{subscription.ends_at.slice(0, 10)}</strong>
                                {subscription.days_remaining !== undefined && ` · ${subscription.days_remaining} dia(s) restantes`}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center border border-[#e4e4e7] p-4">
                            <Users size={14} className="mx-auto text-[#b89b6f]" />
                            <div className="font-display text-3xl text-[#0d1628] mt-2">{users.length}</div>
                            <div className="label-caps mt-1">Utilizadores</div>
                        </div>
                        <div className="text-center border border-[#e4e4e7] p-4">
                            <FolderKanban size={14} className="mx-auto text-[#b89b6f]" />
                            <div className="font-display text-3xl text-[#0d1628] mt-2">{projects.length}</div>
                            <div className="label-caps mt-1">Projetos</div>
                        </div>
                        <div className="text-center border border-[#e4e4e7] p-4">
                            <CreditCard size={14} className="mx-auto text-[#b89b6f]" />
                            <div className="font-display text-3xl text-[#0d1628] mt-2">{subscriptions.length}</div>
                            <div className="label-caps mt-1">Histórico</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Subscriptions history */}
            <section>
                <div className="label-caps">Subscrições</div>
                <div className="card-flat p-0 mt-4 overflow-x-auto" data-testid="company-subs-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-[#e4e4e7]">
                                <th className="text-left p-4 label-caps">Plano</th>
                                <th className="text-left p-4 label-caps">Estado</th>
                                <th className="text-left p-4 label-caps">Método</th>
                                <th className="text-left p-4 label-caps">Pedido</th>
                                <th className="text-left p-4 label-caps">Início</th>
                                <th className="text-left p-4 label-caps">Fim</th>
                                <th className="text-right p-4 label-caps">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subscriptions.length === 0 ? (
                                <tr><td colSpan="7" className="p-8 text-center text-[#52525b]">Sem subscrições ainda.</td></tr>
                            ) : subscriptions.map((s) => (
                                <tr key={s.id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`sub-row-${s.id}`}>
                                    <td className="p-4 text-[#0d1628]">
                                        {s.plan}
                                        {s.payment_reference && (
                                            <div className="text-xs text-[#52525b] font-mono mt-1">{s.payment_reference}</div>
                                        )}
                                        {s.transfer_date && (
                                            <div className="text-xs text-[#52525b] mt-1">Transferência: {s.transfer_date}</div>
                                        )}
                                        {s.internal_notes && (
                                            <div className="text-xs text-[#52525b] mt-1 italic" title={s.internal_notes}>“{s.internal_notes.length > 60 ? s.internal_notes.slice(0, 60) + "…" : s.internal_notes}”</div>
                                        )}
                                        {s.payment_proof_path && (
                                            <a
                                                href={fileUrl(s.payment_proof_path)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs text-[#b89b6f] mt-1 inline-flex items-center gap-1 hover:underline"
                                                data-testid={`proof-link-${s.id}`}
                                            >
                                                <Paperclip size={11} /> {s.payment_proof_filename || "Comprovativo"}
                                            </a>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        <span className={`text-xs border px-2 py-1 capitalize ${STATUS_COLORS[s.status] || STATUS_COLORS.none}`}>{s.status}</span>
                                    </td>
                                    <td className="p-4 text-[#52525b] capitalize">{s.payment_method || "—"}</td>
                                    <td className="p-4 text-[#52525b]">{s.purchased_at?.slice(0, 10)}</td>
                                    <td className="p-4 text-[#52525b]">{s.starts_at?.slice(0, 10) || "—"}</td>
                                    <td className="p-4 text-[#52525b]">{s.ends_at?.slice(0, 10) || "—"}</td>
                                    <td className="p-4 text-right whitespace-nowrap">
                                        {s.status === "pending" && (
                                            <button onClick={() => activate(s.id)} className="btn-ghost" data-testid={`activate-${s.id}`}>
                                                <CheckCircle2 size={14} /> Ativar
                                            </button>
                                        )}
                                        {s.status === "active" && (
                                            <>
                                                <button onClick={() => extend(s.id)} className="btn-ghost" data-testid={`extend-${s.id}`}>
                                                    <Plus size={14} /> Estender
                                                </button>
                                                <button onClick={() => cancel(s.id)} className="btn-ghost text-red-600" data-testid={`cancel-${s.id}`}>
                                                    Cancelar
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Users */}
            <UsersList users={users} />

            {/* Projects */}
            <ProjectsGrid projects={projects} />
        </div>
    );
}


function UsersList({ users }) {
    return (
        <section>
            <div className="label-caps">Utilizadores ({users.length})</div>
            <div className="card-flat p-0 mt-4 overflow-x-auto" data-testid="company-users-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-[#e4e4e7]">
                            <th className="text-left p-4 label-caps">Nome</th>
                            <th className="text-left p-4 label-caps">Email</th>
                            <th className="text-left p-4 label-caps">Papel</th>
                            <th className="text-left p-4 label-caps">Criado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr><td colSpan="4" className="p-8 text-center text-[#52525b]">Sem utilizadores.</td></tr>
                        ) : users.map((u) => (
                            <tr key={u.id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`user-row-${u.id}`}>
                                <td className="p-4 text-[#0d1628]">{u.name}</td>
                                <td className="p-4 text-[#52525b]">{u.email}</td>
                                <td className="p-4"><span className="label-caps">{ROLE_LABELS[u.role] || u.role}</span></td>
                                <td className="p-4 text-[#52525b]">{u.created_at?.slice(0, 10)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


function ProjectsGrid({ projects }) {
    if (projects.length === 0) {
        return (
            <section>
                <div className="label-caps">Projetos (0)</div>
                <div className="card-flat mt-4 text-[#52525b] text-sm" data-testid="empty-projects">Esta empresa ainda não criou projetos.</div>
            </section>
        );
    }
    return (
        <section>
            <div className="label-caps">Projetos ({projects.length})</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="company-projects-grid">
                {projects.map((p) => (
                    <article key={p.id} className="card-flat" data-testid={`project-${p.id}`}>
                        <div className="flex items-center gap-3">
                            <span className="phase-dot active">{p.current_phase || 1}</span>
                            <div className="label-caps">{PHASES[(p.current_phase || 1) - 1]?.title}</div>
                        </div>
                        <h3 className="font-display text-2xl text-[#0d1628] mt-4 leading-tight">{p.name}</h3>
                        <div className="label-caps mt-3">Estado · {p.status || "em_curso"}</div>
                        {p.description && <p className="text-sm text-[#52525b] mt-3 line-clamp-3">{p.description}</p>}
                    </article>
                ))}
            </div>
        </section>
    );
}
